/**
 * Verificación de los documentos técnicos.
 *
 * Todos los técnicos que pida la línea son obligatorios, así que de cualquiera se comprueba que
 * esté aportado. Del presupuesto se mira además el formato, que es el único con criterio
 * definido; lo que haya dentro de los demás queda para la revisión del profesional.
 *
 * A diferencia de los administrativos, los documentos técnicos NO son subsanables: el lineamiento
 * dice que su ausencia es causal de rechazo, también en la primera verificación. Por eso los
 * hallazgos de aquí salen siempre como rechazo, en cualquier etapa.
 */

import { tecnicoAportado } from './puedeInscribirse';

/** Texto oficial: rechazo a del catálogo. */
export const OBSERVACION_TECNICO_FALTANTE =
  'Se rechaza ya que no aporta los documentos técnicos para la evaluación ({documentos_faltantes}). Estos son documentos técnicos no subsanables.';

/** PROPUESTO: no hay texto oficial para el archivo en un formato que no se puede abrir. */
export const OBSERVACION_TECNICO_FORMATO =
  'El archivo aportado en {documento} no está en un formato válido. Debe aportarse en PDF o Excel para poder ser evaluado.';

/**
 * Formatos que se aceptan en el presupuesto.
 *
 * La plataforma, al subsanar, solo admite Excel para el presupuesto y PDF para los demás técnicos.
 * Aquí se acepta cualquiera de los dos a propósito: ser más permisivo que el formulario no genera
 * rechazos indebidos, mientras que ser más estricto sí podría.
 */
const FORMATOS_PRESUPUESTO = ['pdf', 'xlsx', 'xls', 'xlsm'];

/** 'otro' es cualquier tecnico sin criterio propio: igual tiene que estar. */
export type TipoTecnico = 'presupuesto' | 'otro';

const PATRONES_TECNICOS: { tipo: TipoTecnico; patron: RegExp }[] = [
  { tipo: 'presupuesto', patron: /presupuesto/ },
];

export type HallazgoTecnico = {
  /** Posición del documento dentro de documentos_tecnicos */
  indice: number;
  tipo: TipoTecnico;
  nombre: string;
  /** Los técnicos no son subsanables: siempre es rechazo */
  estado: 'rechazar';
  observacion: string;
  confianza: number;
};

const sinTildes = (texto: any): string =>
  String(texto ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

export const clasificarTecnico = (nombre: any): TipoTecnico | null =>
  PATRONES_TECNICOS.find((p) => p.patron.test(sinTildes(nombre)))?.tipo ?? null;

const tieneTexto = (valor: any): boolean => String(valor ?? '').trim() !== '';

export const extensionDe = (archivo: any): string => {
  const partes = String(archivo ?? '').split('.');
  return partes.length > 1 ? partes.pop()!.toLowerCase() : '';
};

export const formatoValidoPresupuesto = (archivo: any): boolean =>
  FORMATOS_PRESUPUESTO.includes(extensionDe(archivo));

/**
 * Revisa los documentos técnicos de una postulación.
 *
 * Solo evalúa los que tiene la postulación: lo que esa línea no exige no está en la lista, así que
 * no se reporta.
 */
export const verificarDocumentosTecnicos = (documentosTecnicos: any): HallazgoTecnico[] => {
  const documentos = Array.isArray(documentosTecnicos) ? documentosTecnicos : [];
  const hallazgos: HallazgoTecnico[] = [];

  documentos.forEach((doc: any, indice: number) => {
    const nombre = String(doc?.tipo_documento ?? doc?.descripcion ?? '').trim();
    const tipo: TipoTecnico = clasificarTecnico(nombre) ?? 'otro';

    /*
      Todos son obligatorios, asi que no hace falta reconocer cual es para
      decir que falta. Si esta aportado se mira por dentro: eso si depende
      del documento, y hoy solo el presupuesto tiene criterio.

      "Escrito y Video" se diligencia en linea, asi que cuenta con tener el
      escrito o el enlace; de eso se encarga tecnicoAportado.
    */
    if (!tecnicoAportado(doc)) {
      hallazgos.push({
        indice,
        tipo,
        nombre,
        estado: 'rechazar',
        observacion: OBSERVACION_TECNICO_FALTANTE.replace(
          '{documentos_faltantes}',
          nombre || 'el documento técnico'
        ),
        confianza: 1,
      });
      return;
    }

    if (tipo === 'presupuesto' && !formatoValidoPresupuesto(doc?.url_participante)) {
      hallazgos.push({
        indice,
        tipo,
        nombre,
        estado: 'rechazar',
        observacion: OBSERVACION_TECNICO_FORMATO.replace(/\{documento\}/g, nombre || 'el presupuesto'),
        confianza: 1,
      });
    }
  });

  return hallazgos;
};

/** Queda en los campos de auditoría, igual que en los administrativos. */
export const MARCADO_POR_TECNICOS = 'Verificación automática';

export type ResultadoTecnicos = {
  /** Copia de los documentos técnicos con las marcas puestas */
  documentos: any[];
  observacion: string;
  rechazados: number;
  /** Documentos que no se tocaron porque ya los había decidido una persona */
  respetados: number;
};

/** Un técnico lo decidió una persona si tiene marca y el autor no es la verificación automática. */
const tieneDecisionHumanaTecnico = (doc: any): boolean => {
  const autor = doc?.aceptado_por ?? doc?.rechazado_por;
  return doc?.checkaceptartecnico !== undefined && tieneTexto(autor) && autor !== MARCADO_POR_TECNICOS;
};

/**
 * Deja las marcas de la verificación sobre una copia de los documentos técnicos.
 * Los técnicos se marcan con checkaceptartecnico, no con los tres estados de los administrativos.
 */
export const aplicarVerificacionTecnicos = (
  documentosTecnicos: any,
  fecha: string,
  opciones: { respetarDecisionHumana?: boolean } = {}
): ResultadoTecnicos => {
  const { respetarDecisionHumana = true } = opciones;
  const originales = Array.isArray(documentosTecnicos) ? documentosTecnicos : [];
  const hallazgos = verificarDocumentosTecnicos(originales);

  const documentos = originales.map((doc: any) => ({ ...doc }));
  let rechazados = 0;
  let respetados = 0;

  hallazgos.forEach((h) => {
    if (respetarDecisionHumana && tieneDecisionHumanaTecnico(originales[h.indice])) {
      respetados++;
      return;
    }

    documentos[h.indice] = {
      ...documentos[h.indice],
      checkaceptartecnico: false,
      rechazado_por: MARCADO_POR_TECNICOS,
      fecha_rechazado: fecha,
      ia_estado: 'rechazar',
      ia_observacion: h.observacion,
      ia_confianza: h.confianza,
      ia_fecha: fecha,
    };
    rechazados++;
  });

  return {
    documentos,
    observacion: hallazgos.map((h) => h.observacion).join('\n'),
    rechazados,
    respetados,
  };
};
